/**
 * 组件对象池，用于复用组件实例以减少内存分配
 */
export class ComponentPool {
    constructor(createFn, resetFn, maxSize = 1000) {
        this.pool = [];
        this.createFn = createFn;
        this.resetFn = resetFn;
        this.maxSize = maxSize;
    }
    /**
     * 获取一个组件实例
     */
    acquire() {
        if (this.pool.length > 0) {
            return this.pool.pop();
        }
        return this.createFn();
    }
    /**
     * 释放一个组件实例回池中
     */
    release(component) {
        if (this.pool.length < this.maxSize) {
            if (this.resetFn) {
                this.resetFn(component);
            }
            this.pool.push(component);
        }
    }
    /**
     * 预填充对象池
     */
    prewarm(count) {
        for (let i = 0; i < count && this.pool.length < this.maxSize; i++) {
            this.pool.push(this.createFn());
        }
    }
    /**
     * 清空对象池
     */
    clear() {
        this.pool.length = 0;
    }
    /**
     * 获取池中可用对象数量
     */
    getAvailableCount() {
        return this.pool.length;
    }
    /**
     * 获取池的最大容量
     */
    getMaxSize() {
        return this.maxSize;
    }
}
/**
 * 全局组件池管理器
 */
export class ComponentPoolManager {
    constructor() {
        this.pools = new Map();
    }
    static getInstance() {
        if (!ComponentPoolManager.instance) {
            ComponentPoolManager.instance = new ComponentPoolManager();
        }
        return ComponentPoolManager.instance;
    }
    /**
     * 注册组件池
     */
    registerPool(componentName, createFn, resetFn, maxSize) {
        this.pools.set(componentName, new ComponentPool(createFn, resetFn, maxSize));
    }
    /**
     * 获取组件实例
     */
    acquireComponent(componentName) {
        const pool = this.pools.get(componentName);
        return pool ? pool.acquire() : null;
    }
    /**
     * 释放组件实例
     */
    releaseComponent(componentName, component) {
        const pool = this.pools.get(componentName);
        if (pool) {
            pool.release(component);
        }
    }
    /**
     * 预热所有池
     */
    prewarmAll(count = 100) {
        for (const pool of this.pools.values()) {
            pool.prewarm(count);
        }
    }
    /**
     * 清空所有池
     */
    clearAll() {
        for (const pool of this.pools.values()) {
            pool.clear();
        }
    }
    /**
     * 获取池统计信息
     */
    getPoolStats() {
        const stats = new Map();
        for (const [name, pool] of this.pools) {
            stats.set(name, {
                available: pool.getAvailableCount(),
                maxSize: pool.getMaxSize()
            });
        }
        return stats;
    }
    /**
     * 获取池利用率信息（用于调试）
     */
    getPoolUtilization() {
        const utilization = new Map();
        for (const [name, pool] of this.pools) {
            const available = pool.getAvailableCount();
            const maxSize = pool.getMaxSize();
            const used = maxSize - available;
            const utilRate = maxSize > 0 ? (used / maxSize * 100) : 0;
            utilization.set(name, {
                used: used,
                total: maxSize,
                utilization: utilRate
            });
        }
        return utilization;
    }
    /**
     * 获取指定组件的池利用率
     */
    getComponentUtilization(componentName) {
        const pool = this.pools.get(componentName);
        if (!pool)
            return 0;
        const available = pool.getAvailableCount();
        const maxSize = pool.getMaxSize();
        const used = maxSize - available;
        return maxSize > 0 ? (used / maxSize * 100) : 0;
    }
}
//# sourceMappingURL=ComponentPool.js.map